import React, { useState } from 'react';
import { bindIntent, proposeIntent, checkIntentDrift } from '../../services/protectBuild';
import IntentPicker from './IntentPicker';
import { EYEBROW, NOTE } from './typeScale';
import Pill from './Pill';

/**
 * The Semantic Decision Layer — the intent this agent is bound to.
 *
 * <h3>Why only approved intents can be attached</h3>
 *
 * A binding is refused unless the template version is APPROVED, and the
 * Execution-Ready gate checks that a binding exists. Offering a draft would be
 * offering a bind the platform rejects, inside a sequence whose promise is that
 * it reports what happened rather than what was hoped.
 *
 * <h3>Why describing one does not finish the job</h3>
 *
 * A new version needs an authored policy and an approver who is not its author.
 * That is enforced in the service and again by a database CHECK, so the most
 * this can do is draft it, submit it for review, and show it as approval
 * pending. Saying anything stronger would be a promise the platform exists to
 * refuse.
 *
 * <h3>Why a template is not the answer on its own</h3>
 *
 * Binding takes a template VERSION id. A template is a name, and approval is
 * what points it at a version — so the choice is made in two acts, with the
 * version's own terms on screen before it is bound. That is also why nothing is
 * preselected here: what an agent may decide is a decision to make, not a
 * default to inherit from whatever sorted first.
 *
 * <h3>Why it can be left for later</h3>
 *
 * Binding is independently set — the platform allows it in any phase, including
 * Act, and the readiness gate checks the binding without ever blocking the act
 * of setting one. So an agent can come under protection now and be given its
 * decision layer when the right one exists, as long as the surface is honest
 * that the gate is still open.
 */
export default function IntentAsk({
  agentId, intents = [], error, scope, onAnswer, busy = false, existing = null,
  onRefresh = null,}) {
  const [picked, setPicked] = useState(null);
  const [authoring, setAuthoring] = useState(false);
  const [form, setForm] = useState({ name: '', description: '' });
  const [working, setWorking] = useState(false);
  const [problem, setProblem] = useState(null);
  const [pending, setPending] = useState(null);

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const attach = async () => {
    if (!picked) return;
    setWorking(true); setProblem(null);
    try {
      await bindIntent(agentId, picked.versionId);
      onAnswer({ bound: true, label: picked.label, versionId: picked.versionId });
    } catch (e) {
      setProblem(e?.message || 'The binding was refused.');
    } finally {
      setWorking(false);
    }
  };

  const propose = async () => {
    setWorking(true); setProblem(null);
    try {
      const out = await proposeIntent({
        name: form.name.trim(),
        description: form.description.trim(),
        orgId: scope?.orgId,
        tenantId: scope?.tenantId,
      });
      setPending(out);
      onAnswer({ bound: false, submitted: out.submitted, label: form.name.trim(),
        templateId: out.templateId });
    } catch (e) {
      setProblem(e?.message || 'The intent could not be drafted.');
    } finally {
      setWorking(false);
    }
  };

  if (pending) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--p7-space-3)' }}>
        <span className="p7-status p7-status--hold" style={{ alignSelf: 'flex-start' }}>
          Approval pending
        </span>
        <p style={NOTE}>
          <strong>{form.name.trim()}</strong> is drafted and
          {pending.submitted ? ' submitted for review' : ' waiting to be submitted'}. You cannot
          approve your own intent — somebody with policy or compliance admin has to approve it,
          and it needs its policy authored first. Until then this agent has no decision layer
          attached, and its readiness gate will say so.
        </p>
        {pending.problem ? (
          <p className="p7-note p7-note--hold" style={{ margin: 0 }}>{pending.problem}</p>
        ) : null}
      </div>
    );
  }

  /* What is bound already. Offering to bind over the top of a live binding is
     how an agent ends up with two, and a refused read is not an unbound agent
     — so neither case falls through to the picker. */
  if (existing?.error) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--p7-space-3)' }}>
        <p className="p7-note p7-note--hold" style={{ margin: 0 }}>{existing.error}</p>
        <div>
          <button type="button" className="p7-btn" disabled={busy}
                  onClick={() => onAnswer({ bound: null, skipped: true })}>
            Carry on
          </button>
        </div>
      </div>
    );
  }

  if (existing?.bound && existing.bindings?.length) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--p7-space-3)' }}>
        <p style={NOTE}>
          Already bound. Its runtime authorization comes from these and nowhere else.
        </p>
        {existing.bindings.map((b) => (
          <BoundBinding key={b.id} agentId={agentId} b={b} busy={busy}
                        onRefresh={onRefresh} />
        ))}
        <div>
          <button type="button" className="p7-btn p7-btn--primary" disabled={busy}
                  onClick={() => onAnswer({ bound: true,
                    label: existing.bindings[0].label })}>
            Keep this
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--p7-space-3)' }}>
      {error ? (
        <p className="p7-note p7-note--deny" style={{ margin: 0 }}>
          Could not read your intents just now. You can still attach one later.
        </p>
      ) : null}

      {!authoring && intents.length === 0 && !error ? (
        <p style={NOTE}>
          No approved intent exists in this tenant yet. Describe one and it goes for approval,
          or attach one later once there is one to attach.
        </p>
      ) : null}

      {!authoring && intents.length > 0 ? (
        <IntentPicker intents={intents} onPick={setPicked} disabled={busy || working} />
      ) : null}

      {authoring ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '9px' }}>
          <Field label="Name" value={form.name} onChange={set('name')}
                 placeholder="renewals-assistant" />
          <Field label="What it is for" value={form.description} onChange={set('description')}
                 placeholder="Reads CRM renewals and drafts summaries." />
          <p style={NOTE}>
            This goes for approval. You cannot approve your own intent, and its policy has to be
            authored before anyone can.
          </p>
        </div>
      ) : null}

      {problem ? <p className="p7-note p7-note--deny" style={{ margin: 0 }}>{problem}</p> : null}

      <div style={{ display: 'flex', gap: '9px', alignItems: 'center', flexWrap: 'wrap' }}>
        {authoring ? (
          <>
            <button type="button" className="p7-btn p7-btn--primary"
                    disabled={busy || working || !form.name.trim() || !form.description.trim()}
                    onClick={propose}>
              {working ? 'Submitting…' : 'Submit for approval'}
            </button>
            <button type="button" className="p7-btn p7-btn--sm" disabled={busy || working}
                    onClick={() => { setAuthoring(false); setProblem(null); }}>
              Cancel
            </button>
          </>
        ) : (
          <>
            {intents.length > 0 ? (
              <button type="button" className="p7-btn p7-btn--primary"
                      disabled={busy || working || !picked} onClick={attach}>
                {working ? 'Binding…' : 'Bind it'}
              </button>
            ) : null}
            <button type="button" className="p7-btn p7-btn--sm" disabled={busy || working}
                    onClick={() => setAuthoring(true)}>
              Describe your own
            </button>
          </>
        )}
        {/* Always available. The gate checks the binding; it never blocks
            setting one, so this is a decision to make later, not a dead end. */}
        <button type="button" className="p7-btn p7-btn--sm" disabled={busy || working}
                onClick={() => onAnswer({ bound: false, deferred: true })}>
          Attach one later
        </button>
      </div>
    </div>
  );
}

/**
 * One binding, and whether it is doing anything.
 *
 * <h3>Bound, checked, and refusing everything</h3>
 *
 * A binding pins the skills its version's matchers resolved to at bind time,
 * and the runtime allows exactly those and nothing else — adding the skill
 * afterwards does not widen it. An empty set therefore matches no skill, and a
 * call that matches no skill is refused. So "bound", "checked on every
 * request" and "refuses every call" are all true together, and saying only the
 * first two reports a working binding that works for nothing.
 *
 * <h3>Why drift can be checked here</h3>
 *
 * Drift is found by a scheduled sweep, so between sweeps the state on screen
 * is one nobody has re-checked. The check needs no more permission than
 * reading the binding did, and it returns only whether drift was found — not
 * the new state — so what is bound is re-read afterwards rather than patched
 * from a field the platform does not send.
 */
function BoundBinding({ agentId, b, busy, onRefresh }) {
  const [checking, setChecking] = useState(false);
  const [checked, setChecked] = useState(null);
  const [problem, setProblem] = useState(null);

  const check = async () => {
    setChecking(true); setProblem(null); setChecked(null);
    const out = await checkIntentDrift(agentId, b.id);
    setChecking(false);
    if (out.error) { setProblem(out.error); return; }
    setChecked(out.drifted);
    onRefresh?.();
  };

  return (
    <div style={{ border: '1px solid var(--p7-line)',
                  borderRadius: 'var(--p7-radius-control)',
                  padding: '11px 13px', background: 'var(--p7-surface-sunken)',
                  display: 'flex', flexDirection: 'column', gap: '7px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
        <span style={{ font: 'var(--p7-text-body)', fontFamily: 'var(--p7-font-sans)',
                       fontWeight: 600, color: 'var(--p7-ink)' }}>{b.label}</span>
        <Pill tone={b.live ? 'allow' : 'hold'}>{b.state}</Pill>
      </div>

      {!b.live ? (
        <p style={NOTE}>
          {/* The platform's own sentence when it wrote one. */}
          {b.staleReason
            || (b.state === 'STALE'
              ? 'The template moved underneath this binding, so it no longer decides '
                + 'what its version said it would.'
              : 'What this binding pointed at is gone. It decides nothing.')}
        </p>
      ) : null}

      <p style={NOTE}>
        {b.frozenSkills === 1 ? '1 skill' : `${b.frozenSkills ?? 0} skills`} frozen at
        bind time{b.narrowedSkills ? `, ${b.narrowedSkills} narrowed back out` : ''}.
        {b.coverage && !b.authorizesNothing
          ? ` Authorizes ${b.coverage.authorized} of ${
            b.coverage.authorized + b.coverage.unauthorized + b.coverage.unevaluated
          } tools this agent can call.`
          : ''}
      </p>

      {b.authorizesNoSkill ? (
        <p className="p7-note p7-note--deny" style={{ margin: 0 }}>
          It allows no skill, so every call that declares this intent is refused. The list is
          frozen on purpose — giving the agent the skills it names does not widen it. Bind it
          again once they exist.
        </p>
      ) : b.authorizesNothing ? (
        <p className="p7-note p7-note--deny" style={{ margin: 0 }}>
          It authorizes none of the tools this agent can call — every call it makes will be
          refused.
        </p>
      ) : null}

      {b.live && b.enforcing === false ? (
        <p className="p7-note p7-note--hold" style={{ margin: 0 }}>
          Bound, but not checked on each request yet. Its rules are compiled onto the agent when
          an approved version is bound; until that has happened nothing is evaluated per call.
        </p>
      ) : null}

      {b.newerVersion?.number ? (
        <p className="p7-note p7-note--hold" style={{ margin: 0 }}>
          A newer approved version, v{b.newerVersion.number}, exists. This agent stays on the
          one it was bound to — nothing moves it quietly — so moving to the newer rules is
          something somebody has to do.
        </p>
      ) : null}

      {onRefresh ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: '9px', flexWrap: 'wrap' }}>
          <button type="button" className="p7-btn p7-btn--sm" disabled={busy || checking}
                  onClick={check}>
            {checking ? 'Checking…' : 'Check for drift'}
          </button>
          <span style={NOTE}>
            {b.lastDriftCheckAt
              ? `Last checked ${whenReadable(b.lastDriftCheckAt)}.`
              : 'Not checked since it was bound.'}
          </span>
        </div>
      ) : null}

      {problem ? (
        <p className="p7-note p7-note--deny" style={{ margin: 0 }}>{problem}</p>
      ) : null}

      {problem === null && checked === false ? (
        <p style={NOTE}>No drift. It still resolves to exactly what it froze.</p>
      ) : null}
      {checked === true ? (
        <p style={NOTE}>Drift found. What is bound has been read again above.</p>
      ) : null}
    </div>
  );
}

/** A stored timestamp said the way somebody would read it. */
function whenReadable(iso) {
  const t = new Date(iso);
  if (Number.isNaN(t.getTime())) return 'earlier';
  return t.toLocaleString(undefined,
    { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function Field({ label, ...rest }) {
  return (
    <label style={{ display: 'block' }}>
      <span style={{ display: 'block', marginBottom: '3px',
                     ...EYEBROW }}>
        {label}
      </span>
      <input
        {...rest}
        style={{ width: '100%', padding: '9px 12px',
                 border: '1px solid var(--p7-line-strong)',
                 borderRadius: 'var(--p7-radius-control)',
                 font: 'var(--p7-text-body)', fontFamily: 'var(--p7-font-sans)' }}
      />
    </label>
  );
}

